import { useState } from 'react';

import useAccountQuery from 'api/queries/useAccountQuery';
import { useEBTCardOperations } from 'shared/ebt/EBTCardForm';

import EBTCardDisplay from './EBTCardDisplay';
import EBTCardEdit from './EBTCardEdit';

/**
 * EBTCard component for managing EBT payment methods in the account section.
 *
 * Handles both display and edit modes for EBT cards, allowing users to view,
 * add, edit, or delete their EBT payment information.
 *
 * @param {Object} props - Component props
 * @param {Object|null} props.ebtCard - EBT card data object containing card information
 * @param {string} [props.ebtCard.last4] - Last 4 digits of the EBT card for display
 * @returns {JSX.Element} Rendered EBT card management interface
 */
export default function EBTCard({ ebtCard }) {
    const { data: account = {} } = useAccountQuery();
    const [isEditing, setIsEditing] = useState(false);

    const { clearErrors, deleteCard, deleteError, isDeleting, isSaving, saveCardWithTokenization, saveError } =
        useEBTCardOperations(account?.subID);

    /**
     * Handles successful card edit/save operations.
     * Exits edit mode and clears any error states.
     */
    const handleEditSuccess = () => {
        setIsEditing(false);
        clearErrors();
    };

    /**
     * Initiates edit mode for the EBT card.
     * Clears any existing errors before switching to edit mode.
     */
    const handleEdit = () => {
        setIsEditing(true);
        clearErrors();
    };

    /**
     * Renders the edit/add button with appropriate text and styling.
     * Shows "Edit" if card exists, "Add" if no card is on file.
     *
     * @param {string} className - CSS classes to apply to the button
     * @returns {JSX.Element} Edit/Add button element
     */
    const editModeCTA = (className) => (
        <button type="button" onClick={handleEdit} className={`text-body-sm link ${className}`}>
            {ebtCard?.last4 ? 'Edit' : 'Add'}
        </button>
    );

    return (
        <>
            <div className="flex items-center justify-between">
                <h2 className="text-body-lg shrink-0 font-grotesk-bold lg:w-[240px] lg:self-start">
                    EBT Payment Method
                </h2>
                {!isEditing && editModeCTA('lg:hidden self-center')}
            </div>
            {isEditing ? (
                <EBTCardEdit
                    customerID={account?.customerID}
                    clearErrors={clearErrors}
                    saveCardWithTokenization={saveCardWithTokenization}
                    isSaving={isSaving}
                    saveError={saveError}
                    setIsEditing={setIsEditing}
                    onSuccess={handleEditSuccess}
                    isEdit={!!ebtCard?.last4}
                />
            ) : (
                <EBTCardDisplay
                    ebtCard={ebtCard}
                    onEdit={handleEdit}
                    deleteCard={deleteCard}
                    isDeleting={isDeleting}
                    deleteError={deleteError}
                    clearErrors={clearErrors}
                    marketCloseDate={account?.marketCloseDate?.date}
                />
            )}
            {!isEditing && editModeCTA('hidden lg:block lg:self-center lg:ml-auto mr-40')}
        </>
    );
}
